<template>
  <aside class="smart-actions-panel" data-testid="smart-actions-side-panel">
    <SmartActionsTabBar v-model="selectedTabId" class="smart-actions-panel__tab-bar" :tabs="tabs" :id-prefix="idPrefix" />

    <section
      v-for="tab in tabs"
      v-show="tab.id === selectedTabId"
      :id="`${idPrefix}-panel-${tab.id}`"
      :key="tab.id"
      role="tabpanel"
      tabindex="-1"
      class="smart-actions-panel__tab"
      :aria-labelledby="`${idPrefix}-tab-${tab.id}`"
      :data-testid="`smart-actions-panel-${tab.id}`"
    >
      <component :is="tab.component" />
    </section>
  </aside>
</template>

<script setup lang="ts">
import { computed, ref, useId, watch, type Component } from 'vue';
import { storeToRefs } from 'pinia';

import AiActionsContent from './AiActionsContent.vue';
import MeetingDetails from './meeting/MeetingDetails.vue';
import ChaptersSection from './ChaptersSection.vue';
import ToDoContent from './ToDoContent.vue';
import SmartActionsTabBar, { type SmartActionsTab } from './SmartActionsTabBar.vue';

import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { useChapters } from '@/modules/viewer/composables/useChapters';
import { useVideoAi } from '@/modules/viewer/composables/useVideoAi';
import { useToDo } from '@/modules/viewer/composables/useToDo';

type PanelTab = SmartActionsTab & { component: Component };

const { isMeeting } = storeToRefs(useViewerItemStore());
const { hasChapters } = useChapters();
const { hasAiAddon } = useVideoAi();
const { isToDoAvailable, count: toDoCount } = useToDo();
const idPrefix = useId();

const hasToDoTab = computed(() => hasAiAddon.value && isToDoAvailable.value);

// The theater/mobile entry point for this panel labels itself "Meeting
// Details" for meetings (VideoOwnerLayout's theaterPanelLabel) — land on
// that tab by default so opening it doesn't show "AI Actions" instead of
// what the label promised.
const defaultTabId = computed(() => (isMeeting.value ? 'meeting-details' : 'ai-actions'));

const tabs = computed<PanelTab[]>(() => {
  const list: PanelTab[] = [{ id: 'ai-actions', label: 'AI Actions', fullLabel: 'AI Actions', icon: 'general.stars', component: AiActionsContent }];
  if (isMeeting.value) {
    list.push({ id: 'meeting-details', label: 'Details', fullLabel: 'Meeting Details', icon: 'general.info', component: MeetingDetails });
  }
  if (hasChapters.value) {
    list.push({ id: 'chapters', label: 'Chapters', fullLabel: 'Chapters', icon: 'general.hamburger', component: ChaptersSection });
  }
  if (hasToDoTab.value) {
    list.push({ id: 'to-do', label: 'To-Do', fullLabel: 'To-Do', icon: 'general.tick', badgeCount: toDoCount.value, component: ToDoContent });
  }
  return list;
});

const selectedTabId = ref(defaultTabId.value);

watch(defaultTabId, id => {
  selectedTabId.value = id;
});

// A tab can disappear while selected (chapters cleared, To-Do entitlement
// lost); never leave the selection pointing at a panel that is no longer there.
watch(
  tabs,
  list => {
    if (list.some(tab => tab.id === selectedTabId.value)) {
      return;
    }
    selectedTabId.value = list.some(tab => tab.id === defaultTabId.value) ? defaultTabId.value : list[0].id;
  },
  { immediate: true }
);
</script>

<style scoped>
.smart-actions-panel {
  @apply flex flex-col bg-white dark:bg-zinc-800 rounded-xl border border-zinc-200 dark:border-zinc-700 w-full p-4 sm:p-5;
}

.smart-actions-panel__tab {
  @apply pt-4;
}

/* The compact header spans the full panel width so four labels fit at a
   300px panel; the panel padding still applies to the tab content. */
.smart-actions-panel__tab-bar {
  @apply -mx-4 -mt-4 sm:-mx-5 sm:-mt-5 px-2 pt-3;
}
</style>
